import { useState, useEffect, useRef } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClipboard } from "@fortawesome/pro-regular-svg-icons"
import dayjs from "dayjs"
import { ConsultationCalendarEvent } from "../scheduleApi"
import { HOUR_HEIGHT, MIN_EVENT_HEIGHT, snapTo15Minutes, START_HOUR } from "./weekViewModel"
import { ELLIPSIS_TEXT_STYLE, CARD_COMPACT_MAX_HEIGHT, getCardOuterStyle, getCardInnerStyle } from "./weekViewCardStyles"
import WeekViewCardResizeHandle from "./WeekViewCardResizeHandle"
import ConsultationPopoverContent from "../ConsultationPopoverContent"
import { HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"

export type ConsultationColumn = {
  consultation: ConsultationCalendarEvent
  column: number
  totalColumns: number
}

export type Props = {
  consultationColumn: ConsultationColumn
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void
  onResize?: (consultation: ConsultationCalendarEvent, newDurationMinutes: number) => void
  onResizingChange?: (isResizing: boolean) => void
  topOffset: number
}

const getConsultationTopOffset = (consultation: ConsultationCalendarEvent): number => {
  const start = dayjs(consultation.start)
  const hours = start.hour()
  const minutes = start.minute()
  const totalMinutes = (hours - START_HOUR) * 60 + minutes
  return (totalMinutes / 60) * HOUR_HEIGHT
}

const getConsultationHeight = (consultation: ConsultationCalendarEvent): number => {
  const start = dayjs(consultation.start)
  const end = dayjs(consultation.end)
  const durationMinutes = end.diff(start, 'minutes')
  const height = (durationMinutes / 60) * HOUR_HEIGHT
  return Math.max(height, MIN_EVENT_HEIGHT)
}

const WeekViewConsultation = ({ consultationColumn, onConsultationClick, onResize, onResizingChange, topOffset }: Props) => {
  const { consultation, column, totalColumns } = consultationColumn
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `consultation-${consultation.id}`,
    data: { consultation, isConsultation: true },
  })

  const resizeId = `consultation-${consultation.id}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
    transform: resizeTransform,
  } = useDraggable({
    id: resizeId,
    data: { consultation, isResize: true, isConsultation: true },
  })

  const [isHovering, setIsHovering] = useState(false)
  const lastResizeTransformY = useRef<number>(0)

  const startTime = dayjs(consultation.start)
  const endTime = dayjs(consultation.end)
  const originalDurationMinutes = endTime.diff(startTime, 'minutes')

  const resizeDelta = resizeTransform?.y ?? 0

  useEffect(() => {
    if (resizeTransform?.y !== undefined) {
      lastResizeTransformY.current = resizeTransform.y
    }
  }, [resizeTransform?.y])

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      const deltaMinutes = (lastResizeTransformY.current / HOUR_HEIGHT) * 60
      const snappedDeltaMinutes = snapTo15Minutes(deltaMinutes)
      const newDuration = Math.max(15, originalDurationMinutes + snappedDeltaMinutes)
      if (onResize && newDuration !== originalDurationMinutes) {
        onResize(consultation, newDuration)
      }
      lastResizeTransformY.current = 0
    },
  })

  const formatTimeRange = () => {
    if (isResizing) {
      const deltaMinutes = (resizeDelta / HOUR_HEIGHT) * 60
      const snappedDeltaMinutes = snapTo15Minutes(deltaMinutes)
      const newDuration = Math.max(15, originalDurationMinutes + snappedDeltaMinutes)
      const newEndTime = startTime.add(newDuration, 'minute')
      return `${startTime.format('h:mm A')} - ${newEndTime.format('h:mm A')}`
    }
    return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`
  }

  const borderColor = consultation.organizerColor || '#e0e0e0'

  const top = getConsultationTopOffset(consultation) - topOffset
  const baseHeight = getConsultationHeight(consultation)

  const snappedResizeDelta = isResizing
    ? (snapTo15Minutes((resizeDelta / HOUR_HEIGHT) * 60) / 60) * HOUR_HEIGHT
    : 0
  const height = Math.max(MIN_EVENT_HEIGHT, baseHeight + snappedResizeDelta)

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  const widthPercent = totalColumns > 0 ? 100 / totalColumns : 100
  const leftPercent = totalColumns > 0 ? column * widthPercent : 0

  const isCompact = baseHeight < CARD_COMPACT_MAX_HEIGHT

  const outerStyle: React.CSSProperties = {
    ...getCardOuterStyle({ top, height, widthPercent, leftPercent, isDragging, isResizing, transform }),
    backgroundColor: borderColor,
  }
  const innerStyle = getCardInnerStyle({ isCompact })

  return (
    <Popover
      content={<ConsultationPopoverContent consultation={consultation} />}
      open={isHovering && !isDragging && !isResizing}
      placement="right"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (!isResizing && onConsultationClick) {
            onConsultationClick(consultation)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={outerStyle}
      >
        <div style={innerStyle}>
        {isCompact ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
            <FontAwesomeIcon icon={faClipboard} style={{ color: '#999', fontSize: 10, flexShrink: 0 }} />
            <span style={{ ...ELLIPSIS_TEXT_STYLE, fontWeight: 500, fontSize: 11, flex: 1, minWidth: 0 }}>
              {consultation.customer.displayName}
            </span>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#999', fontSize: 10, lineHeight: 1.2 }}>
              <FontAwesomeIcon icon={faClipboard} style={{ fontSize: 10 }} />
              <span>CONSULTATION</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ ...ELLIPSIS_TEXT_STYLE, fontWeight: 500, fontSize: 12, flex: 1, minWidth: 0 }}>
                {consultation.customer.displayName}
              </span>
            </div>
            <span style={{ ...ELLIPSIS_TEXT_STYLE, color: '#666', fontSize: 11 }}>
              {formatTimeRange()}
            </span>
          </>
        )}

        {(isHovering || isResizing) && onResize && (
          <WeekViewCardResizeHandle
            setNodeRef={setResizeNodeRef}
            listeners={resizeListeners}
            attributes={resizeAttributes}
            color={borderColor}
          />
        )}
        </div>
      </div>
    </Popover>
  )
}

export default WeekViewConsultation
